<template>
  <Teleport to="body">
    <div v-if="isVisible" class="hc-overlay">
      <div class="hc-modal">

        <!-- Header -->
        <div class="hc-header">
          <div>
            <div class="hc-eyebrow">Historial de compras</div>
            <h2 class="hc-title">{{ cliente?.nombre_completo }}</h2>
          </div>
          <button class="modal-close-x" @click="$emit('close')">✕</button>
        </div>

        <!-- KPI Cards -->
        <div class="hc-kpis" v-if="resumen">
          <div class="hc-kpi">
            <div class="hc-kpi-val">L. {{ fmt(resumen.totalGastado) }}</div>
            <div class="hc-kpi-lbl">Total gastado</div>
          </div>
          <div class="hc-kpi">
            <div class="hc-kpi-val">{{ resumen.totalTransacciones }}</div>
            <div class="hc-kpi-lbl">Compras realizadas</div>
          </div>
          <div class="hc-kpi">
            <div class="hc-kpi-val">L. {{ fmt(resumen.promedioPorCompra) }}</div>
            <div class="hc-kpi-lbl">Promedio por compra</div>
          </div>
          <div class="hc-kpi">
            <div class="hc-kpi-val">{{ fmtFecha(resumen.ultimaCompra) }}</div>
            <div class="hc-kpi-lbl">Última compra</div>
          </div>
        </div>

        <!-- Body -->
        <div class="hc-body">
          <div v-if="cargando" class="hc-empty">
            <div class="hc-spinner"></div>
            <span>Cargando historial...</span>
          </div>

          <div v-else-if="!ventas.length" class="hc-empty">
            <i class="bi bi-receipt" style="font-size:2rem;color:#aaa"></i>
            <p>Este cliente no tiene compras registradas.</p>
          </div>

          <template v-else>
            <!-- Tabla de ventas -->
            <div class="hc-table-wrap">
              <table class="hc-table">
                <thead>
                  <tr>
                    <th>Factura</th>
                    <th>Fecha</th>
                    <th>Método</th>
                    <th>Vendedor</th>
                    <th class="text-right">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="venta in ventasPaginadas" :key="venta.id_venta">
                    <tr class="hc-row" :class="{ 'hc-row-expanded': expandida === venta.id_venta }">
                      <td class="hc-mono">{{ venta.codigo_factura }}</td>
                      <td>{{ fmtFechaCorta(venta.fecha) }}</td>
                      <td>
                        <span class="hc-badge" :class="badgeClass(venta.metodo_pago)">
                          {{ venta.metodo_pago }}
                        </span>
                      </td>
                      <td>{{ venta.vendedor }}</td>
                      <td class="text-right hc-mono hc-total">L. {{ fmt(venta.total) }}</td>
                      <td>
                        <button class="hc-expand-btn" @click="toggleExpand(venta.id_venta)">
                          <i :class="expandida === venta.id_venta ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                        </button>
                      </td>
                    </tr>
                    <!-- Detalle expandido -->
                    <tr v-if="expandida === venta.id_venta" class="hc-detail-row">
                      <td colspan="6">
                        <div class="hc-detail">
                          <table class="hc-detail-table">
                            <thead>
                              <tr>
                                <th>Producto</th>
                                <th class="text-right">Cant.</th>
                                <th class="text-right">Precio unit.</th>
                                <th class="text-right">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(p, i) in venta.productos" :key="i">
                                <td>{{ p.nombre }}</td>
                                <td class="text-right hc-mono">{{ p.cantidad }}</td>
                                <td class="text-right hc-mono">L. {{ fmt(p.precio) }}</td>
                                <td class="text-right hc-mono">L. {{ fmt(p.subtotal ?? p.cantidad * p.precio) }}</td>
                              </tr>
                            </tbody>
                          </table>
                          <div class="hc-totales">
                            <div class="hc-totales-row">
                              <span>Subtotal</span>
                              <span>L. {{ fmt(venta.sub_total) }}</span>
                            </div>
                            <div class="hc-totales-row" v-if="venta.descuento > 0">
                              <span>Descuento</span>
                              <span class="text-red">-L. {{ fmt(venta.descuento) }}</span>
                            </div>
                            <div class="hc-totales-row">
                              <span>ISV (15%)</span>
                              <span>L. {{ fmt(venta.total_isv) }}</span>
                            </div>
                            <div class="hc-totales-row hc-totales-final">
                              <span>Total</span>
                              <span>L. {{ fmt(venta.total) }}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <!-- Paginación -->
            <div class="hc-pagination" v-if="totalPaginas > 1">
              <button class="hc-page-btn" :disabled="pagina === 1" @click="pagina--">Anterior</button>
              <span class="hc-page-info">{{ pagina }} / {{ totalPaginas }}</span>
              <button class="hc-page-btn" :disabled="pagina === totalPaginas" @click="pagina++">Siguiente</button>
            </div>
          </template>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script>
import { getHistorialCliente } from '../../services/clienteSolicitudes';

export default {
  name: 'HistorialClienteModal',
  props: {
    isVisible: { type: Boolean, required: true },
    cliente:   { type: Object, default: null },
  },
  emits: ['close'],
  data() {
    return {
      cargando:  false,
      resumen:   null,
      ventas:    [],
      expandida: null,
      pagina:    1,
      porPagina: 10,
    };
  },
  computed: {
    totalPaginas() {
      return Math.ceil(this.ventas.length / this.porPagina);
    },
    ventasPaginadas() {
      const ini = (this.pagina - 1) * this.porPagina;
      return this.ventas.slice(ini, ini + this.porPagina);
    },
  },
  watch: {
    isVisible(v) {
      if (v && this.cliente) this.cargar();
      else this.reset();
    },
  },
  methods: {
    async cargar() {
      this.cargando = true;
      try {
        const data    = await getHistorialCliente(this.cliente.id_cliente);
        this.resumen  = data.resumen;
        this.ventas   = data.ventas;
      } catch (_e) {
        this.ventas  = [];
        this.resumen = null;
      } finally {
        this.cargando = false;
      }
    },
    reset() {
      this.resumen  = null;
      this.ventas   = [];
      this.expandida = null;
      this.pagina   = 1;
    },
    toggleExpand(id) {
      this.expandida = this.expandida === id ? null : id;
    },
    fmt(v) {
      return Number(v || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtFecha(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleDateString('es-HN', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    fmtFechaCorta(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    badgeClass(metodo) {
      const m = (metodo || '').toLowerCase();
      if (m === 'efectivo')      return 'badge-efectivo';
      if (m === 'transferencia') return 'badge-transfer';
      if (m === 'crédito' || m === 'credito') return 'badge-credito';
      if (m === 'mixto')         return 'badge-mixto';
      return 'badge-otro';
    },
  },
};
</script>

<style scoped>
.hc-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1200;
  padding: 24px 12px;
  overflow-y: auto;
}

.hc-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 860px;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: auto;
}

/* ── HEADER ── */
.hc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #e9ecef;
  gap: 16px;
}
.hc-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 2px;
}
.hc-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

/* ── KPI CARDS ── */
.hc-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-bottom: 1px solid #e9ecef;
}
.hc-kpi {
  padding: 16px 20px;
  border-right: 1px solid #e9ecef;
  text-align: center;
}
.hc-kpi:last-child { border-right: none; }
.hc-kpi-val {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1a202c;
  line-height: 1.2;
}
.hc-kpi-lbl {
  font-size: 11px;
  color: #6c757d;
  margin-top: 3px;
}

/* ── BODY ── */
.hc-body {
  flex: 1;
  overflow: auto;
  padding: 0;
}
.hc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  color: #6c757d;
}
.hc-spinner {
  width: 28px; height: 28px;
  border: 3px solid #e9ecef;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── TABLA ── */
.hc-table-wrap { overflow-x: auto; }
.hc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.hc-table thead th {
  background: #f8f9fa;
  padding: 10px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: #6c757d;
  border-bottom: 1px solid #dee2e6;
  white-space: nowrap;
}
.hc-table tbody td {
  padding: 11px 14px;
  border-bottom: 1px solid #f1f3f5;
  color: #374151;
}
.hc-row:hover td { background: #f8fafc; }
.hc-row-expanded td { background: #f0f7ff; }
.hc-mono { font-variant-numeric: tabular-nums; font-family: monospace; font-size: 12px; }
.hc-total { font-weight: 700; color: #1a202c; }
.text-right { text-align: right; }
.text-red { color: #dc2626; }

/* ── EXPAND BUTTON ── */
.hc-expand-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
  min-width: auto;
  transition: background .15s;
}
.hc-expand-btn:hover { background: #e9ecef; }

/* ── DETALLE EXPANDIDO ── */
.hc-detail-row td { padding: 0 !important; background: #f8fafc !important; }
.hc-detail { padding: 16px 20px 20px; display: flex; flex-direction: column; gap: 16px; }
.hc-detail-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.hc-detail-table thead th {
  padding: 6px 10px;
  background: #e9ecef;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: #6c757d;
}
.hc-detail-table tbody td { padding: 6px 10px; border-bottom: 1px solid #e9ecef; }

.hc-totales { margin-left: auto; width: 260px; display: flex; flex-direction: column; gap: 4px; }
.hc-totales-row { display: flex; justify-content: space-between; font-size: 12.5px; color: #374151; }
.hc-totales-final { font-weight: 700; font-size: 14px; color: #1a202c; border-top: 1px solid #dee2e6; padding-top: 6px; margin-top: 4px; }

/* ── BADGES ── */
.hc-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
}
.badge-efectivo  { background: #d1fae5; color: #065f46; }
.badge-transfer  { background: #dbeafe; color: #1e40af; }
.badge-credito   { background: #fef3c7; color: #92400e; }
.badge-mixto     { background: #ede9fe; color: #4c1d95; }
.badge-otro      { background: #f3f4f6; color: #374151; }

/* ── PAGINACIÓN ── */
.hc-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
}
.hc-page-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 13px;
  cursor: pointer;
  color: #374151;
  min-width: auto;
  transition: background .15s;
}
.hc-page-btn:hover:not(:disabled) { background: #e9ecef; }
.hc-page-btn:disabled { opacity: .5; cursor: not-allowed; }
.hc-page-info { font-size: 13px; color: #6c757d; }

/* ── DARK MODE ── */
.dark .hc-modal { background: #1a2035; color: #e2e8f0; }
.dark .hc-header { border-color: #2d3748; }
.dark .hc-eyebrow { color: #718096; }
.dark .hc-title { color: #e2e8f0; }
.dark .hc-kpis { border-color: #2d3748; }
.dark .hc-kpi { border-color: #2d3748; }
.dark .hc-kpi-val { color: #e2e8f0; }
.dark .hc-kpi-lbl { color: #718096; }
.dark .hc-table thead th { background: #253047; border-color: #2d3748; color: #718096; }
.dark .hc-table tbody td { border-color: #2d3748; color: #cbd5e0; }
.dark .hc-row:hover td { background: #1e2a3a; }
.dark .hc-row-expanded td { background: #1a2a3a; }
.dark .hc-total { color: #e2e8f0; }
.dark .hc-expand-btn { color: #718096; }
.dark .hc-expand-btn:hover { background: #2d3748; }
.dark .hc-detail-row td { background: #151e2d !important; }
.dark .hc-detail-table thead th { background: #253047; }
.dark .hc-detail-table tbody td { border-color: #2d3748; }
.dark .hc-totales-row { color: #cbd5e0; }
.dark .hc-totales-final { color: #e2e8f0; border-color: #2d3748; }
.dark .hc-pagination { border-color: #2d3748; }
.dark .hc-page-btn { background: #253047; border-color: #2d3748; color: #cbd5e0; }
.dark .hc-page-btn:hover:not(:disabled) { background: #2d3748; }
.dark .hc-page-info { color: #718096; }
.dark .hc-empty { color: #718096; }
.dark .badge-efectivo { background: #064e3b; color: #6ee7b7; }
.dark .badge-transfer { background: #1e3a5f; color: #93c5fd; }
.dark .badge-credito  { background: #451a03; color: #fcd34d; }
.dark .badge-mixto    { background: #2e1065; color: #c4b5fd; }
.dark .badge-otro     { background: #374151; color: #d1d5db; }
</style>
